import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Menu,
  RefreshCw,
  Store,
  Trash2,
} from "lucide-react";
import type {
  TmallExperienceMetric,
  TmallExperienceWindow,
} from "@ailan/contracts";
import {
  clearAllDemoData,
  getTmallExperienceDashboard,
  syncTmallExperience,
} from "../api/client";
import { ExperiencePlatformTabs } from "../components/ExperiencePlatformTabs";

const sourceLabels = {
  DEMO: "演示数据",
  LIVE: "浏览器实采",
  CONNECTOR: "企业同步",
} as const;

function formatDateTime(value: string) {
  if (!value || value.startsWith("1970-")) return "尚未采集";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function metricPosition(score: number) {
  return `${Math.max(0, Math.min(100, (score - 3) / 2 * 100))}%`;
}

function windowScore(window: TmallExperienceWindow) {
  if (window.overallScore !== null) return window.overallScore;
  const weightedSections = window.sections.filter((section) => section.weight > 0 && section.metrics.length);
  const totalSectionWeight = weightedSections.reduce((sum, section) => sum + section.weight, 0);
  if (!totalSectionWeight) return null;
  return weightedSections.reduce((total, section) => {
    const metricWeight = section.metrics.reduce((sum, metric) => sum + metric.weight, 0);
    const score = metricWeight
      ? section.metrics.reduce((sum, metric) => sum + metric.score * metric.weight, 0) / metricWeight
      : 0;
    return total + score * section.weight / totalSectionWeight;
  }, 0);
}

function ExperienceMetricRow({ metric }: { metric: TmallExperienceMetric }) {
  return (
    <article className="experienceMetricRow">
      <div className="experienceMetricName">
        <strong>{metric.name}</strong>
        <span>权重 {metric.weight}%</span>
        {metric.note && <em>{metric.note}</em>}
      </div>
      <div className="experienceMetricValue">
        <strong>{metric.value}</strong>
        <span>{metric.warning ? "建议关注" : "指标正常"}</span>
      </div>
      <div className="experienceScale">
        <div className="experienceScaleLine">
          <i style={{ width: metricPosition(metric.score) }} />
          <b style={{ left: metricPosition(metric.score) }}>{metric.score.toFixed(1)}分</b>
        </div>
        <div className="experienceScalePoints">
          {metric.scale.map((point) => (
            <span key={`${point.score}-${point.benchmark}`}>
              <strong>{point.score.toFixed(1)}分</strong>
              <small>{point.benchmark}</small>
            </span>
          ))}
        </div>
      </div>
      <div className="experienceTarget">
        <span>目标值</span>
        <strong>{metric.target ?? "无"}</strong>
      </div>
      <div className="experienceChange">
        <details>
          <summary>{metric.change ? `较前一日 ${metric.change}` : "暂无前日数据"}</summary>
          {metric.previousScore !== null && <span>前日得分：{metric.previousScore.toFixed(1)} 分</span>}
          {metric.changeReason && <em>分析：{metric.changeReason}</em>}
        </details>
      </div>
    </article>
  );
}

export function TmallExperiencePage({ onMenuOpen }: { onMenuOpen: () => void }) {
  const queryClient = useQueryClient();
  const [selectedStoreName, setSelectedStoreName] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const dashboard = useQuery({
    queryKey: ["tmall-experience-dashboard"],
    queryFn: getTmallExperienceDashboard,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
  const syncMutation = useMutation({
    mutationFn: syncTmallExperience,
    onSuccess: (data) => {
      queryClient.setQueryData(["tmall-experience-dashboard"], data);
      setNotice(data.windows.length ? "体验分数据已刷新" : "尚未收到店铺采集窗口数据");
    },
    onError: (error) => setNotice(error instanceof Error ? error.message : "体验分同步失败，请检查采集窗口"),
  });
  const clearMutation = useMutation({
    mutationFn: clearAllDemoData,
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setNotice("全部演示数据已清除，真实数据已保留");
    },
    onError: () => setNotice("演示数据清理失败"),
  });

  const stores = useMemo(() => {
    const windows = dashboard.data?.windows ?? [];
    const connectedNames = new Set(windows.map((window) => window.storeName));
    return [
      ...windows.map((window) => ({ id: window.id, name: window.storeName, connected: true })),
      ...(dashboard.data?.storeDirectory ?? [])
        .filter((store) => !connectedNames.has(store.name))
        .map((store) => ({ id: store.id, name: store.name, connected: false })),
    ];
  }, [dashboard.data?.storeDirectory, dashboard.data?.windows]);
  useEffect(() => {
    if (!stores.some((store) => store.name === selectedStoreName)) {
      setSelectedStoreName(stores[0]?.name ?? "");
    }
  }, [selectedStoreName, stores]);

  const activeWindow = dashboard.data?.windows.find((window) => window.storeName === selectedStoreName);
  const scores = useMemo(
    () => (dashboard.data?.windows ?? []).map(windowScore).filter((score): score is number => score !== null),
    [dashboard.data?.windows],
  );
  const averageScore = scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null;
  const metricCount = activeWindow?.sections.reduce((sum, section) => sum + section.metrics.length, 0) ?? 0;
  const warningCount = activeWindow?.sections.reduce(
    (sum, section) => sum + section.metrics.filter((metric) => metric.warning).length,
    0,
  ) ?? 0;
  const demoCount = dashboard.data?.windows.filter((window) => window.source === "DEMO").length ?? 0;

  if (dashboard.isLoading) {
    return <main className="main"><div className="centerState"><RefreshCw className="spin" />正在读取天猫体验分...</div></main>;
  }
  if (dashboard.isError || !dashboard.data) {
    return <main className="main"><div className="centerState errorState"><AlertTriangle />无法读取体验分数据<button className="button" onClick={() => void dashboard.refetch()}>重新加载</button></div></main>;
  }

  return (
    <main className="main experienceMain">
      <header className="topbar">
        <button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button>
        <strong>店铺后台体验分</strong>
        <span>天猫真实体验分</span>
        <i className={dashboard.data.sync.enabled ? "liveDot" : "offlineDot"} />
        <small>{dashboard.data.sync.enabled ? "采集窗口已连接" : "等待采集窗口"}</small>
      </header>
      <div className="experienceContent">
        <ExperiencePlatformTabs current="tmall" />
        <section className="experienceHead">
          <div>
            <h1>天猫真实体验分</h1>
            <p>店铺窗口数据 · 更新至 {formatDateTime(dashboard.data.lastSyncedAt)}</p>
          </div>
          <div className="experienceActions">
            <button className="button" onClick={() => window.open("/#experience", "_blank", "noopener,noreferrer")}><ExternalLink />新窗口打开</button>
            <button className="button dangerButton" disabled={!demoCount || clearMutation.isPending} onClick={() => {
              if (window.confirm("只清除全部演示/模拟数据，CSV 导入、手工真实数据和自动采集数据都会保留。确认继续？")) clearMutation.mutate();
            }}><Trash2 />{clearMutation.isPending ? "清除中..." : "清除全部演示数据"}</button>
            <button className="button primary" disabled={syncMutation.isPending} onClick={() => syncMutation.mutate()}><RefreshCw className={syncMutation.isPending ? "spin" : ""} />{syncMutation.isPending ? "同步中..." : "同步真实数据"}</button>
          </div>
        </section>

        <section className={`experienceSyncBand ${dashboard.data.sync.enabled ? "connected" : ""}`}>
          {dashboard.data.sync.enabled ? <CheckCircle2 /> : <AlertTriangle />}
          <div><strong>{dashboard.data.sync.enabled ? "店铺采集窗口运行中" : "尚未连接真实采集窗口"}</strong><span>{dashboard.data.sync.message}</span></div>
          <span>每分钟自动采集</span>
        </section>

        <nav className="experienceWindowTabs" aria-label="店铺采集窗口">
          {stores.map((store) => {
            const window = dashboard.data.windows.find((item) => item.storeName === store.name);
            return (
            <button key={store.id} type="button" className={store.name === selectedStoreName ? "active" : ""} onClick={() => setSelectedStoreName(store.name)}>
              <Store />
              <span><strong>{store.name}</strong><small>{window?.accountName ?? "等待店铺体验分数据"}</small></span>
              <i className={window?.status === "CONNECTED" ? "connected" : ""} />
              {window ? <em className={window.source.toLowerCase()}>{sourceLabels[window.source]}</em> : <em>待采集</em>}
            </button>
          );})}
        </nav>

        {activeWindow ? (
          <>
            <section className="experienceKpis">
              <article><span>当前窗口</span><strong>{activeWindow.storeName}</strong><small>{sourceLabels[activeWindow.source]}</small></article>
              <article><span>综合体验分</span><strong>{windowScore(activeWindow)?.toFixed(2) ?? "--"}<em> / 5</em></strong><small>{activeWindow.overallScore !== null ? "天猫后台官方总分" : "按分组权重计算"}</small><details className="experienceKpiChange"><summary>{activeWindow.overallChange ? `较前一日 ${activeWindow.overallChange}` : "暂无前日数据"}</summary>{activeWindow.previousScore !== null && <span>前日得分：{activeWindow.previousScore.toFixed(2)} 分</span>}{activeWindow.changeReason && <em>分析：{activeWindow.changeReason}</em>}</details></article>
              <article><span>指标数量</span><strong>{metricCount}</strong><small>宝贝、物流、服务指标</small></article>
              <article><span>待关注指标</span><strong className={warningCount ? "warningText" : ""}>{warningCount}</strong><small>当前窗口诊断结果</small></article>
              <article><span>全部窗口均分</span><strong>{averageScore?.toFixed(2) ?? "--"}<em> / 5</em></strong><small>{dashboard.data.windows.length} 个店铺窗口</small></article>
            </section>

            <section className="experienceWindowMeta">
              <div><strong>{activeWindow.storeName}</strong><span>{activeWindow.accountName}</span></div>
              <span>后台更新时间：{activeWindow.updatedDate}</span>
              <span>采集时间：{formatDateTime(activeWindow.collectedAt)}</span>
            </section>

            {activeWindow.sections.map((section) => (
              <section className="experienceSection" key={section.key}>
                <header><i /><h2>{section.name}</h2><span>{section.weight ? `权重 ${section.weight}%` : "附加分"}</span>{section.score !== null && <strong>{section.score.toFixed(2)} 分</strong>}{section.score !== null && <details className="experienceSectionChange"><summary>{section.change ? `较前一日 ${section.change}` : "暂无前日数据"}</summary>{section.previousScore !== null && <span>前日得分：{section.previousScore.toFixed(2)} 分</span>}{section.changeReason && <em>分析：{section.changeReason}</em>}</details>}</header>
                {section.metrics.length ? (
                  <div>{section.metrics.map((metric) => <ExperienceMetricRow key={metric.key} metric={metric} />)}</div>
                ) : (
                  <div className="experienceEmptySection"><strong>{activeWindow.bonusScore?.toFixed(1) ?? "--"} 分</strong><span>当前窗口附加分</span></div>
                )}
              </section>
            ))}
          </>
        ) : (
          <section className="experienceEmpty"><Store /><strong>{selectedStoreName || "还没有店铺体验分数据"}</strong><span>该店铺已保留在目录中，采集窗口写入后将在这里展示体验分</span></section>
        )}
      </div>
      {notice && <button className="toast" onClick={() => setNotice(null)}>{notice}</button>}
    </main>
  );
}
